import {useMemo} from "react"
import {useAppContext} from "./useAppContext";
import {contentService} from "../api/contentService";
import {CrossSellType, ImageType, Nullable} from "../definitions/types";
import {BundleModel} from "../models/view/bundleModel";
import {useSuspenseQuery} from "@apollo/client";
import {defaultRatingStatistics} from "../utils/constants";
import {mapper} from "../utils/helpers";
import {CrossSellModel} from "../models/domain/crossSellModel";

export const useHighLevelCalloutQuery = () => {
    const {productId, primaryId, currencyCode} = useAppContext();

    const {error, data} = useSuspenseQuery(contentService.product.highLevelCallout, {
        errorPolicy: "all",
        variables: {
            productId,
            currencyCode
        }
    });

    const worksWellWithCrossSells = useMemo(() => [
        ...data?.product?.worksWellWith?.filter((crossSell): crossSell is CrossSellType => crossSell !== null).map(crossSell => mapper.crossSellToModel(crossSell)) ?? [] as CrossSellModel[],
        ...data?.product?.compatibleWith?.filter((crossSell): crossSell is CrossSellType => crossSell !== null).map(crossSell => mapper.crossSellToModel(crossSell)) ?? [] as CrossSellModel[],
    ], [data]);

    const bundleModel = useMemo(() => {
        // Deconstruct the product object into the properties we need with default values
        const {
            isDynamicDimension,
            designerId,
            price,
            title,
            sku,
            collectionName,
            variantPath,
            bundledWith,
            stockInformation,
            media: {
                images: {
                    dot4b
                }
            }
        } = data?.product ?? {
            isDynamicDimension: false,
            designerId: undefined,
            price: undefined,
            title: undefined,
            sku: undefined,
            collectionName: undefined,
            variantPath: undefined,
            minimumQuantity: undefined,
            bundledWith: [] as Nullable<CrossSellType>[],
            stockInformation: null,
            media: {
                images: {
                    dot4b: [] as Nullable<ImageType>[]
                }
            }
        };

        const minimumQuantity = stockInformation?.minQuantity ?? 1;

        // IF the current product is a dynamic dimension, don't render anything
        if (isDynamicDimension) return undefined;

        const items = [...bundledWith.filter((item): item is CrossSellType => item !== null)];

        // If there are no cross-sells, don't render anything
        if (items.length === 0) return undefined;

        // If there is no price, don't render anything
        if (!price) return undefined;

        // Get the first image from the media object (if it exists)
        const image = dot4b.filter((image): image is ImageType => image !== null)[0];

        // If there is no image, don't render anything
        if (!image) return undefined;

        // Add the current product to the front of the array
        items.unshift({
            productId: productId,
            primaryId: primaryId,
            designerId: designerId,
            title: title,
            sku: sku,
            collectionName: collectionName,
            path: variantPath,
            customizeIt: isDynamicDimension || !!designerId,
            minQuantity: minimumQuantity,
            imageSrc: image.url,
            imageAlt: image.alt,
            imageTitle: image.title,
            unitPrice: price.unitPrice,
            salePrice: price.salePrice,
            saleAmount: price.saleAmount,
            salePercentage: price.salePercentage,
            price: price.price,
            onSale: price.onSale,
            isDynamicDimension: isDynamicDimension,
            ratingStatistics: defaultRatingStatistics
        });

        return new BundleModel(items);
    }, [data]);

    return {
        error: error?.message,
        bundleModel,
        worksWellWithCrossSells
    }
}